var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useState } from 'react';
import { FaCrown, FaTrash, FaUserShield, FaPlus, FaSpinner } from 'react-icons/fa';
import { AdminPermission, ALL_ADMIN_PERMISSIONS } from '@common/contracts';
import { createAdmin, deleteAdmin, listAdmins, updateAdminPermissions } from '../../api/admins';
import './AdminsPanel.scss';
// Human labels for the granular permissions. ManageEntities also gates the AI
// provider keys, task routing and generation limits — name it so an admin
// hunting for "why can't I save the API key" actually finds this checkbox.
const PERMISSION_LABELS = {
    [AdminPermission.ManageEntities]: 'Libraries & AI keys',
    [AdminPermission.ManageChallenges]: 'Manage challenges',
    [AdminPermission.ModerateLevels]: 'Moderate levels',
    [AdminPermission.ManageAdmins]: 'Manage admins',
};
// Shown on hover — what each checkbox actually unlocks.
const PERMISSION_HINTS = {
    [AdminPermission.ManageEntities]: 'Edit the shared libraries (node types, resources, agents, scripts, shields, NPCs, prompts) AND the AI settings: provider API keys, task routing, models, generation limits. Without it those screens are read-only.',
    [AdminPermission.ManageChallenges]: 'Create, edit, open/close and archive challenges.',
    [AdminPermission.ModerateLevels]: 'Approve or reject levels in the moderation queue.',
    [AdminPermission.ManageAdmins]: 'Add and remove admins, and edit their permissions.',
};
function errorMessage(err, fallback) {
    var _a, _b;
    const e = err;
    return ((_b = (_a = e === null || e === void 0 ? void 0 : e.response) === null || _a === void 0 ? void 0 : _a.data) === null || _b === void 0 ? void 0 : _b.message) || (e === null || e === void 0 ? void 0 : e.message) || fallback;
}
/**
 * Admin management surface (only reachable with the ManageAdmins permission — the
 * backend enforces every mutation). Add/remove admins and edit their permission
 * sets; the root super-admin is shown but can't be edited or removed.
 */
export function AdminsPanel() {
    const [admins, setAdmins] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);
    // New-admin form.
    const [newUsername, setNewUsername] = useState('');
    const [newPerms, setNewPerms] = useState(new Set());
    const load = useCallback(() => __awaiter(this, void 0, void 0, function* () {
        setLoading(true);
        try {
            setAdmins(yield listAdmins());
            setError(null);
        }
        catch (err) {
            setError(errorMessage(err, 'Failed to load admins'));
        }
        finally {
            setLoading(false);
        }
    }), []);
    useEffect(() => { void load(); }, [load]);
    const handleAdd = () => __awaiter(this, void 0, void 0, function* () {
        if (!newUsername.trim() || busy)
            return;
        setBusy(true);
        try {
            yield createAdmin({ username: newUsername.trim(), permissions: [...newPerms] });
            setNewUsername('');
            setNewPerms(new Set());
            setError(null);
            yield load();
        }
        catch (err) {
            setError(errorMessage(err, 'Failed to add admin'));
        }
        finally {
            setBusy(false);
        }
    });
    const handleTogglePermission = (admin, perm) => __awaiter(this, void 0, void 0, function* () {
        if (admin.isRoot || busy)
            return;
        const next = new Set(admin.permissions);
        if (next.has(perm))
            next.delete(perm);
        else
            next.add(perm);
        setBusy(true);
        try {
            yield updateAdminPermissions(admin.username, { permissions: [...next] });
            setError(null);
            yield load();
        }
        catch (err) {
            setError(errorMessage(err, 'Failed to update permissions'));
        }
        finally {
            setBusy(false);
        }
    });
    const handleRemove = (admin) => __awaiter(this, void 0, void 0, function* () {
        if (admin.isRoot || busy)
            return;
        if (!window.confirm(`Remove admin @${admin.username}? They lose all admin access.`))
            return;
        setBusy(true);
        try {
            yield deleteAdmin(admin.username);
            setError(null);
            yield load();
        }
        catch (err) {
            setError(errorMessage(err, 'Failed to remove admin'));
        }
        finally {
            setBusy(false);
        }
    });
    const toggleNewPerm = (perm) => {
        setNewPerms(prev => {
            const next = new Set(prev);
            if (next.has(perm))
                next.delete(perm);
            else
                next.add(perm);
            return next;
        });
    };
    return (_jsxs("div", { className: "admins-panel", children: [_jsxs("div", { className: "admins-panel__header", children: [_jsxs("h2", { children: [_jsx(FaUserShield, {}), " Administrators"] }), _jsx("p", { children: "Grant granular admin access. The root super-admin can't be edited or removed." })] }), error && _jsx("div", { className: "admins-panel__error", children: error }), _jsxs("div", { className: "admins-panel__add", children: [_jsx("input", { type: "text", placeholder: "Telegram username (e.g. stalker42)", value: newUsername, disabled: busy, onChange: (e) => setNewUsername(e.target.value), onKeyDown: (e) => { if (e.key === 'Enter')
                            void handleAdd(); } }), _jsx("div", { className: "admins-panel__perm-choices", children: ALL_ADMIN_PERMISSIONS.map(perm => (_jsxs("label", { title: PERMISSION_HINTS[perm], className: newPerms.has(perm) ? 'on' : '', children: [_jsx("input", { type: "checkbox", checked: newPerms.has(perm), disabled: busy, onChange: () => toggleNewPerm(perm) }), PERMISSION_LABELS[perm]] }, perm))) }), _jsxs("button", { className: "admins-panel__add-btn", disabled: busy || !newUsername.trim(), onClick: handleAdd, children: [busy ? _jsx(FaSpinner, { className: "spin" }) : _jsx(FaPlus, {}), " Add admin"] })] }), loading ? (_jsxs("div", { className: "admins-panel__loading", children: [_jsx(FaSpinner, { className: "spin" }), " Loading\u2026"] })) : (_jsx("ul", { className: "admins-panel__list", children: admins.map(admin => (_jsxs("li", { className: `admin-row ${admin.isRoot ? 'is-root' : ''}`, children: [_jsxs("div", { className: "admin-row__who", children: [admin.isRoot ? _jsx(FaCrown, { title: "Root super-admin" }) : _jsx(FaUserShield, {}), _jsxs("b", { children: ["@", admin.username] }), admin.isRoot && _jsx("span", { className: "admin-row__root-tag", children: "root \u00B7 all permissions" })] }), !admin.isRoot && (_jsx("div", { className: "admin-row__perms", children: ALL_ADMIN_PERMISSIONS.map(perm => (_jsxs("label", { title: PERMISSION_HINTS[perm], className: admin.permissions.includes(perm) ? 'on' : '', children: [_jsx("input", { type: "checkbox", checked: admin.permissions.includes(perm), disabled: busy, onChange: () => handleTogglePermission(admin, perm) }), PERMISSION_LABELS[perm]] }, perm))) })), !admin.isRoot && (_jsx("button", { className: "admin-row__remove", disabled: busy, onClick: () => handleRemove(admin), "aria-label": `Remove ${admin.username}`, children: _jsx(FaTrash, {}) }))] }, admin.username))) }))] }));
}
export default AdminsPanel;
